/**
 * Cómo se arman los datos derivados del syllabus para imprimirlo: las sesiones
 * de cada módulo a partir del formato de jornada, y las fechas de entrega a
 * partir del `weekOffset` de cada tarea.
 *
 * VIVE FUERA DEL BOTÓN a propósito. Es armado de datos, no interfaz, y el botón
 * arrastra MUI, i18next y el SDK web de Firebase: importarlo desde cualquier
 * otro lado —por ejemplo `scripts/render-syllabus-pdf.tsx`, que renderiza el
 * documento sin navegador— no es posible. Dos copias de esta lógica darían dos
 * cronogramas distintos para el mismo curso.
 */
import type { SyllabusPdfData } from '../pages/sis/components/SyllabusPdfDocument';
import type { SyllabusPdfLabels } from './syllabus-pdf-labels';
import { layoutDay } from '@dosfilos/domain';
import { formatScriptureRefs } from './scripture-refs';

export interface RichSession {
  id: string;
  dayIndex: number;
  durationMinutes?: number;
  topic: string;
  scriptureRefs?: { book: string; chapter: number; verses?: string }[];
}

export interface RichModule {
  id: string;
  name: string;
  axisTopic: string;
  weekOffsets?: number[];
  dayOffsets?: { weekIndex: number; weekday: string }[];
  sessions?: RichSession[];
}

export interface RichSessionFormat {
  weekdayTemplates: {
    weekday: string;
    startTime: string;
    endTime: string;
    segments: { kind: 'section' | 'break'; durationMinutes: number; label?: string }[];
  }[];
}

export interface RichAssignment {
  weekOffset: number;
  name: string;
  type?: string;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function clockToMin(s: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return null;
  return parseInt(m[1]!, 10) * 60 + parseInt(m[2]!, 10);
}

function minToClock(n: number): string {
  const h = Math.floor(n / 60) % 24;
  const mm = n % 60;
  return `${pad2(h)}:${pad2(mm)}`;
}

/**
 * Materialize the module-by-module session timetable using the template's
 * own week/day structure + sessionFormat. Used in the editor preview where
 * there's no real Term to derive absolute dates from — the "date" column
 * shows "Sem N · weekday" so the author can visually confirm sequencing.
 *
 * Sessions are laid out with `layoutDay` (@dosfilos/domain), the single rule
 * shared with the generator and the editor's timeline (S6): MULTIPLE
 * sessions can fit inside a single section-kind segment, and each session's
 * `durationMinutes` (or the remaining segment budget) chooses how much of the
 * segment it consumes.
 */
export function materializeModuleSchedule(
  modules: RichModule[],
  sessionFormat: RichSessionFormat,
  labels: SyllabusPdfLabels,
): SyllabusPdfData['moduleSchedule'] {
  const wdMap = new Map(sessionFormat.weekdayTemplates.map((w) => [w.weekday, w]));
  return modules.map((m) => {
    // Group sessions by dayIndex preserving authoring order.
    const byDay = new Map<number, RichSession[]>();
    (m.sessions ?? []).forEach((s) => {
      const arr = byDay.get(s.dayIndex) ?? [];
      arr.push(s);
      byDay.set(s.dayIndex, arr);
    });
    const out: SyllabusPdfData['moduleSchedule'][number]['sessions'] = [];
    // Walk each declared day-of-module in the same order as the editor
    // (dayOffsets[]), not the dictionary order of byDay — so the PDF rows
    // appear top-down the way they were authored.
    (m.dayOffsets ?? []).forEach((day, dayIndex) => {
      const sList = byDay.get(dayIndex) ?? [];
      const weekdayKey = day?.weekday ?? '';
      const wd = wdMap.get(weekdayKey);
      // Match editor display: "sem N" where N = day.weekIndex + 1.
      const week = (day?.weekIndex ?? dayIndex) + 1;
      const dayLabel = labels.weekWithDay(week, weekdayKey);
      const start = (wd ? clockToMin(wd.startTime) : null) ?? 0;
      // S6: la única regla de reparto (la misma del generador y del editor).
      const layout = layoutDay(wd?.segments ?? [], sList);
      for (const p of layout.placed) {
        const sess = p.session;
        out.push({
          date: dayLabel,
          startTime: minToClock(start + p.startMinute),
          endTime: minToClock(start + p.endMinute),
          topic: sess.topic,
          ...(sess.scriptureRefs && sess.scriptureRefs.length > 0
            ? { scriptureRefs: formatScriptureRefs(sess.scriptureRefs) }
            : {}),
        });
      }
      // Overflow: sessions authored beyond what segments can hold. Mirror
      // the editor's "Sobra — sin bloque" behaviour with em-dash clock so
      // the author sees the topic but is alerted that no slot was assigned.
      for (const u of layout.unplaced) {
        out.push({
          date: dayLabel,
          startTime: '—',
          endTime: '—',
          topic: u.session.topic,
        });
      }
    });
    return { moduleId: m.id, sessions: out };
  });
}

/**
 * Synthesize human-readable deadline labels from each assignment's
 * `weekOffset`. The preview never has a real Term, so we show "Sem N" in
 * the date column instead of "—".
 */
export function synthesizeDeadlines(
  assignments: RichAssignment[],
  labels: SyllabusPdfLabels,
): SyllabusPdfData['deadlines'] {
  return assignments
    .slice()
    .sort((a, b) => a.weekOffset - b.weekOffset)
    .map((a) => ({
      week: a.weekOffset + 1,
      date: labels.weekShort(a.weekOffset + 1),
      entry: a.name,
    }));
}
